import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const TRIAL_EXPIRED_MESSAGE = "Trial expired. Screen module is disabled for this account.";

const AlertContext = createContext({
  showAlert: () => {},
  showNotification: () => {},
  notify: {
    success: () => {},
    warning: () => {},
    danger: () => {},
    info: () => {},
  },
});

const inferTitle = (title, message) => {
  if (title) return title;
  if (String(message || "").trim() === TRIAL_EXPIRED_MESSAGE) return "Trial Expired";
  return "Notice";
};

export function AlertProvider({ children }) {
  const [alertState, setAlertState] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const notificationIdRef = useRef(0);

  const showAlert = useCallback((message, title) => {
    setAlertState({
      title: inferTitle(title, message),
      message: String(message || ""),
    });
  }, []);

  const closeNotification = useCallback((id) => {
    setNotifications((current) => current.filter((item) => item.id !== id));
  }, []);

  const showNotification = useCallback((message, toneOrOptions = "success", options = {}) => {
    const optionBag =
      toneOrOptions && typeof toneOrOptions === "object"
        ? toneOrOptions
        : { ...options, tone: toneOrOptions };
    const tone = String(optionBag.tone || "success").toLowerCase();
    const normalizedTone =
      tone === "danger" || tone === "error" || tone === "delete"
        ? "danger"
        : tone === "warning" || tone === "warn"
          ? "warning"
          : tone === "info"
            ? "info"
            : "success";
    const duration = Number(optionBag.duration ?? 3500);
    const id = `${Date.now()}-${notificationIdRef.current++}`;

    setNotifications((current) => [
      ...current,
      {
        id,
        tone: normalizedTone,
        title: optionBag.title || "",
        message: String(message || ""),
      },
    ]);

    if (duration > 0) {
      window.setTimeout(() => closeNotification(id), duration);
    }

    return id;
  }, [closeNotification]);

  const notify = useMemo(() => ({
    success: (message, options) => showNotification(message, { ...options, tone: "success" }),
    warning: (message, options) => showNotification(message, { ...options, tone: "warning" }),
    danger: (message, options) => showNotification(message, { ...options, tone: "danger" }),
    info: (message, options) => showNotification(message, { ...options, tone: "info" }),
  }), [showNotification]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const originalAlert = window.alert.bind(window);
    window.alert = (message) => {
      showAlert(message);
    };
    return () => {
      window.alert = originalAlert;
    };
  }, [showAlert]);

  return (
    <AlertContext.Provider value={{ showAlert, showNotification, notify }}>
      {children}

      {alertState ? (
        <div className="modal-overlay global-alert-overlay" onClick={() => setAlertState(null)}>
          <div className="modal modal-sm global-alert-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{alertState.title}</h2>
            <p className="modal-message">{alertState.message}</p>
            <div className="modal-actions">
              <button type="button" className="dark-btn" onClick={() => setAlertState(null)}>
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {notifications.length ? (
        <div className="global-notification-stack" aria-live="polite" aria-atomic="false">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`global-notification global-notification-${notification.tone}`}
              role="status"
            >
              <div className="global-notification-copy">
                {notification.title ? <strong>{notification.title}</strong> : null}
                <span>{notification.message}</span>
              </div>
              <button
                type="button"
                className="global-notification-close"
                onClick={() => closeNotification(notification.id)}
                aria-label="Dismiss notification"
              >
                x
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </AlertContext.Provider>
  );
}

export function useAlert() {
  return useContext(AlertContext);
}
